/**
 * Saving Apple Pay or Google Pay (Add payment method → Apple Pay / Google Pay) with Stripe's Express
 * Checkout Element: the person presses the wallet's own button and approves in its payment sheet;
 * Stripe.js confirms a SetupIntent (the bank's £0 check — nothing is charged) and the portal keeps
 * the card it made, as it does a card typed in. The API reads the wallet off the card at Stripe, so
 * what the browser sends can't change how the card is used: a saved Apple Pay card is only ever
 * charged for renewals (Apple Pay's terms); a Google Pay card is used like any other card.
 */
import { useEffect, useRef } from "react";

import { portal } from "@/lib/api";
import { SETUP_OPTIONS, type Wallet } from "@/lib/stripe";
import type { PaymentMethod } from "@/lib/types";

import { useWalletButton, WALLET_NAMES, type ApplePayRecurring, type WalletEntry } from "./useWalletButton";

export {
  WALLET_WAIT_MS,
  type ApplePayRecurring,
  type WalletEntry,
  type WalletStatus,
} from "./useWalletButton";

const SETUP_ELEMENTS = { ...SETUP_OPTIONS, currency: "gbp" };

export function useWalletSetup(
  orgId: string,
  wallet: Wallet,
  {
    recurring = null,
    makeDefault,
    onSaved,
  }: { recurring?: ApplePayRecurring | null; makeDefault: boolean; onSaved: (method: PaymentMethod) => void },
): WalletEntry {
  // Read when the person approves, so ticking the box doesn't redraw the wallet's button.
  const defaultRef = useRef(makeDefault);
  const savedRef = useRef(onSaved);
  useEffect(() => {
    defaultRef.current = makeDefault;
    savedRef.current = onSaved;
  });
  const name = WALLET_NAMES[wallet];
  return useWalletButton(orgId, wallet, {
    recurring,
    elementsOptions: SETUP_ELEMENTS,
    incomplete: `${name} didn't finish. Nothing was saved.`,
    flow: {
      start: async () => {
        const setup = await portal.preparePaymentMethod(orgId);
        if (!setup.client_secret) throw new Error(`${name} isn't available right now.`);
        return setup.client_secret;
      },
      confirm: async (stripe, elements, clientSecret) => {
        const result = await stripe.confirmSetup({
          elements,
          clientSecret,
          redirect: "if_required",
          confirmParams: { return_url: window.location.href },
        });
        if (result.error || !result.setupIntent)
          throw new Error(result.error?.message ?? `${name} didn't finish. Nothing was saved.`);
        if (result.setupIntent.status !== "succeeded")
          throw new Error("Your bank did not approve the card. Try again, or add a card instead.");
        const pm = result.setupIntent.payment_method;
        return typeof pm === "string" ? pm : pm.id;
      },
      // Keeps the card Stripe made. If this fails after Stripe has it, the same card can be kept again
      // ("Try again") instead of going through the wallet a second time and leaving the first behind.
      finish: async (token) => {
        const method = await portal.addPaymentMethod(orgId, {
          token,
          make_default: defaultRef.current,
          save: true,
        });
        savedRef.current(method);
      },
    },
  });
}
